import * as ko from "knockout";
import PromiseRequest from "../../../req/PromiseRequest";
import {IRequestObject} from "../../../req/index";
import {ILogin, IMessageData} from "../../../index";
import {getObjectDisplayName, isPartOf} from "../../../util/misc";
import {ButtonManager} from "../../../util/ButtonManager";
import {getCancelButton, getContinueButton} from "../../../constants/RouteDefinitions";
import {IDialogOptionsComponent} from "../../dialog/index";
import {PLEASE_WAIT_MESSAGE, REQUEST_MESSAGE} from "../../../constants/Language";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import multi_tokenHtml from './multi-token.html?raw';

export const template = multi_tokenHtml

interface IParams {
    tokens: string[]
    dialog: IDialogOptionsComponent
}

type TStep = 'init' | 'choose' | 'process'

type TLogin = [ILogin, string]

export class viewModel {

    private requests!: IRequestObject[]
    private logins!: TLogin[]
    private loginsError!: string[]
    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false)
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null)
    private readonly pageButtons = new ButtonManager();
    private readonly selected: KnockoutObservable<TLogin | null> = ko.observable(null);
    private readonly step: KnockoutObservable<TStep> = ko.observable<TStep>('init');
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()

    constructor(private params: IParams) {
        this.messageData({message: PLEASE_WAIT_MESSAGE})
        this.pageButtons.add(getCancelButton(() => {
            this.params.dialog.onError?.()
            this.params.dialog.doClose?.()
        }))
        this.pageButtons.add(getContinueButton({
            onClick: () => this.step('process'),
            enabled: () => !!this.selected(),
            validator: () => this.isStep('choose'),
        }))
        this.kosub.subscribe(this.step, this.onStep.bind(this))
        this.onStep(this.step())
    }

    private onStep(step: TStep) {
        switch (step) {
            case "init": {
                this.isReady(false)
                this.messageData({message: REQUEST_MESSAGE})
                this.logins = []
                this.loginsError = []
                this.requests?.forEach(o => o.promise.abort())

                this.requests = this.params.tokens.map(token => {
                    const promise = new PromiseRequest('login')
                    const request = promise
                        .request({token: token}, true, undefined, {
                            errorHandler: () => {
                                this.loginsError.push(token)
                            },
                        })
                        .then((data: ILogin) => {
                            this.logins.push([data, token])
                        })
                        .catch((reason: string) => {
                            this.loginsError.push(token)
                        })
                    return {promise, request}
                })

                Promise.all(this.requests.map(o => o.request))
                    .then(() => {
                        this.messageData(null)
                        this.step('choose')
                    })
                break;
            }
            case "choose": {
                this.isReady(true)
                break;
            }
            case "process": {
                this.isReady(false)
                this.messageData({message: PLEASE_WAIT_MESSAGE})
                this.requests = []
                const selected = this.selected();
                if (!selected) {
                    return;
                }
                this.requests = this.logins
                    // not selected
                    .filter(o => o[1] != selected[1])
                    .map(o => {
                        // Log out non-selected tokens, best-effort: a token the server no longer
                        // knows answers returncode 1, and a rejection here would strand the dialog
                        // on the spinner. errorHandler keeps that off not_authenticated, which
                        // would tear down the session we are about to log in with.
                        const promise = new PromiseRequest('logout')
                        const request = promise
                            .request({token: o[1]}, false, undefined, {errorHandler: () => undefined})
                            .catch(() => undefined)
                        return {promise, request}
                    })

                Promise.all(this.requests.map(o => o.request))
                    .then(() => {
                        this.messageData(null)
                        this.isReady(false)
                        const loginObj = this.selected();
                        if (!loginObj) {
                            return;
                        }
                        this.params.dialog.onSuccess?.(loginObj)
                        this.params.dialog.doClose?.()
                    })
                break;
            }
        }
    }

    private getDisplayName(obj: ILogin): string {
        return getObjectDisplayName(obj) ?? ''
    }

    protected isStep = isPartOf.bind(this, this.step)

    dispose() {
        this.requests?.forEach(o => o.promise.abort())
    }
}